import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useMsal,
} from '@azure/msal-react';
import {
	StackCompat as Stack,
	initializeIcons,
	ThemeProviderCompat as ThemeProvider,
} from 'components/FluentUICompat';
import {BrowserRouterCompat} from 'components/apolloTestingCompat';
import {DashboardLayout, Layout} from 'appShell/Layout';
import {CommandsProvider} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {NotFoundPage} from 'components/NotFoundPage';
import {PAGE_PATHS} from 'pages';
import React from 'react';
import {Route, Routes} from 'react-router-dom';
import {themes} from 'theme';
import {ProjectDetailsPageParams} from 'features/VehicleProjects/vehicleProjectPagePaths';
import {createRequirementHref} from 'features/Requirements/requirementLinks.utils';
import {createRegulationHref} from 'features/Regulations/components/regulationLinks.utils';
import {
	FieldsToCreatePathOfBaselineDetailsPage,
	createPathOfBaselineDetailsPage,
} from 'features/Baselines/BaselineDetailsPage/BaselineDetailsPage.utils';
import {createPathOfBaselinesComparisonPage} from 'features/Baselines/BaselineComparisonPage/BaselineComparisonPage.utils';
import {createPathOfSimulationDetailsPage} from 'features/Simulations/SimulationDetailsPage/simulationsPagePaths.utils';
import {createBaselineListPagePath} from './features/Baselines/BaselineListPage/BaselinesPage.utils';
import {withStore} from 'react-context-hook';
import GlobalAnnouncementPopup from 'features/GlobalAnnouncements/GlobalAnnouncementPopup';
import {
	AdminActiveTypeMarketsPage,
	AdminAttachmentCategoriesPage,
	AdminBodyworkVariantsPage,
	AdminCategoriesPage,
	AdminCountriesPage,
	AdminDataProtectionPage,
	AdminDocumentSourcesPage,
	AdminDriveVariantsPage,
	AdminEngineVariantsPage,
	AdminGearboxVariantsPage,
	AdminGlobalAnnouncementPage,
	DistributionListSubscriptionsPage,
	AdminKeywordsPage,
	AdminMarketsPage,
	AdminNotificationPage,
	AdminPermissionsPage,
	AdminPcmsClustersPage,
	AdminReferencePage,
	AdminRegulationsClustersPage,
	AdminSystemLevelsPage,
	AdminTagsPage,
	AdminVehicleCategoriesPage,
	AdminVexClustersPage,
	AdminVkoPage,
	BaselineComparisonPage,
	BaselineDetailsPage,
	BaselineListPage,
	DashboardPageV2,
	FavoritesPage,
	GetexAsRegDocDetailsPage,
	GetexRegulationPage,
	ParagraphDetailsPage,
	PogisDocumentPage,
	QuestionPage,
	RecentsPage,
	RegulationDetail,
	RegulationsPage,
	RegulatoryDocumentArchivePage,
	RegulatoryDocumentDetailsPage,
	RegulatoryDocumentEditorPage,
	RequirementDetailsPage,
	RequirementsPage,
	ReportsPage,
	SearchPage,
	SimulationDetailsPage,
	SimulationsPage,
	SubscriptionsPage,
	VehicleProjectDetailsPage,
	VehicleProjectsPage,
	VkoVexListPage,
} from './lazyPages';

function App() {
	const genAndModelSeriesParams: ProjectDetailsPageParams = {
		modelSeries: ':modelSeries',
		generation: ':generation',
	};

	const createFieldsToCreatePathOfBaselineDetailsPage =
		(): FieldsToCreatePathOfBaselineDetailsPage => {
			return {...genAndModelSeriesParams, baselineId: ':id'};
		};

	const createRoutePathOfBaselineDetailsPage = (): string => {
		const fields: FieldsToCreatePathOfBaselineDetailsPage =
			createFieldsToCreatePathOfBaselineDetailsPage();
		return createPathOfBaselineDetailsPage(fields);
	};

	const createRoutePathOfBaselineComparisonPage = (): string => {
		const baseFields: FieldsToCreatePathOfBaselineDetailsPage =
			createFieldsToCreatePathOfBaselineDetailsPage();
		return createPathOfBaselinesComparisonPage({
			...baseFields,
			idOfBaselineToCompareTo: ':idOfBaselineToCompareTo',
		});
	};

	const msal = useMsal();

	return (
		<>
			<AuthenticatedTemplate>
				<ThemeProvider theme={themes}>
					<GlobalAnnouncementPopup />
					<CommandsProvider>
						<BrowserRouterCompat>
							<React.Suspense fallback={<Layout />}>
								<Routes>
									<Route element={<DashboardLayout />}>
										<Route path='/' element={<DashboardPageV2 />} />
									</Route>
									<Route element={<Layout />}>
										<Route path='/favorites' element={<FavoritesPage />} />
										<Route path='/recents' element={<RecentsPage />} />
										<Route
											path='/subscriptions'
											element={<SubscriptionsPage />}
										/>
										<Route
											path={createRegulationHref(':regulationId')}
											element={<RegulationDetail />}
										/>
										<Route
											path='/regulatoryDocuments/:regulatoryDocumentId'
											element={<RegulatoryDocumentDetailsPage />}
										/>
										<Route
											path='/question/:questionId'
											element={<QuestionPage />}
										/>
										<Route
											path='/regulations/:regulationId/:regulatoryDocumentId'
											element={<RegulatoryDocumentEditorPage />}
										/>
										<Route
											path='/regulations/:regulationId/:regulatoryDocumentId/paragraphs'
											element={<RegulatoryDocumentDetailsPage />}
										/>
										<Route
											path='/regulations/:regulationId/:regulatoryDocumentId/paragraphs/:paragraphId'
											element={<ParagraphDetailsPage />}
										/>
										<Route
											path={PAGE_PATHS.regulations}
											element={<RegulationsPage />}
										/>
										{/* ET164-2526: Jessica over Teams: "We only have and need the POGISArchive. The other archive is old and we don't need it" */}
										<Route
											path='/regulatoryDocumentsArchive'
											element={<RegulatoryDocumentArchivePage />}
										/>
										<Route
											path='/pogisArchive'
											element={<PogisDocumentPage />}
										/>
										<Route
											path={PAGE_PATHS.requirements}
											element={<RequirementsPage />}
										/>
										<Route
											path={createRequirementHref(':requirementId')}
											element={<RequirementDetailsPage />}
										/>
										<Route
											path={PAGE_PATHS.vehicleProjects}
											element={<VehicleProjectsPage />}
										/>
										<Route path='/vkoVexList' element={<VkoVexListPage />} />
										<Route
											path='/vehicleProjects/:modelSeries/:generation'
											element={<VehicleProjectDetailsPage />}
										/>
										<Route
											path={createBaselineListPagePath(genAndModelSeriesParams)}
											element={<BaselineListPage />}
										/>
										<Route
											path={createRoutePathOfBaselineDetailsPage()}
											element={<BaselineDetailsPage />}
										/>
										<Route
											path={createRoutePathOfBaselineComparisonPage()}
											element={<BaselineComparisonPage />}
										/>
										<Route
											path={PAGE_PATHS.simulations}
											element={<SimulationsPage />}
										/>
										<Route
											path={createPathOfSimulationDetailsPage(':id')}
											element={<SimulationDetailsPage />}
										/>
										<Route
											path='/reports/regulatory-documents'
											element={<ReportsPage />}
										/>
										<Route path='/search' element={<SearchPage />} />
										<Route
											path='/admin/markets'
											element={<AdminMarketsPage />}
										/>
										<Route
											path='/admin/regulationsClusters'
											element={<AdminRegulationsClustersPage />}
										/>
										<Route
											path='/admin/vehicleCategories'
											element={<AdminVehicleCategoriesPage />}
										/>
										<Route
											path='/admin/driveVariants'
											element={<AdminDriveVariantsPage />}
										/>
										<Route
											path='/admin/engineVariants'
											element={<AdminEngineVariantsPage />}
										/>
										<Route
											path='/admin/gearboxVariants'
											element={<AdminGearboxVariantsPage />}
										/>
										<Route
											path='/admin/bodyworkVariants'
											element={<AdminBodyworkVariantsPage />}
										/>
										<Route
											path='/admin/activeTypeMarkets'
											element={<AdminActiveTypeMarketsPage />}
										/>
										<Route
											path='/admin/categories'
											element={<AdminCategoriesPage />}
										/>
										<Route
											path='/admin/keywords'
											element={<AdminKeywordsPage />}
										/>
										<Route
											path='/admin/vexClusters'
											element={<AdminVexClustersPage />}
										/>
										<Route
											path='/admin/systemLevels'
											element={<AdminSystemLevelsPage />}
										/>
										<Route path='/admin/tags' element={<AdminTagsPage />} />
										<Route
											path='/admin/pcmsClusters'
											element={<AdminPcmsClustersPage />}
										/>
										<Route
											path='/admin/attachmentCategories'
											element={<AdminAttachmentCategoriesPage />}
										/>
										<Route
											path='/admin/dataProtection'
											element={<AdminDataProtectionPage />}
										/>
										<Route
											path='/admin/references'
											element={<AdminReferencePage />}
										/>
										<Route
											path='/admin/documentSources'
											element={<AdminDocumentSourcesPage />}
										/>
										<Route
											path='/admin/countries'
											element={<AdminCountriesPage />}
										/>
										<Route
											path='/admin/notifications'
											element={<AdminNotificationPage />}
										/>
										<Route
											path='/admin/distributionListSubscriptions'
											element={<DistributionListSubscriptionsPage />}
										/>
										<Route
											path='/admin/permissions'
											element={<AdminPermissionsPage />}
										/>
										<Route
											path='/admin/announcements'
											element={<AdminGlobalAnnouncementPage />}
										/>
										<Route path='/admin/vkos' element={<AdminVkoPage />} />{' '}
										<Route
											path='/getexRegulations'
											element={<GetexRegulationPage />}
										/>{' '}
										<Route
											path='/getexRegulations/:regulatoryDocumentId'
											element={<GetexAsRegDocDetailsPage />}
										/>
										<Route path='*' element={<NotFoundPage />} />
									</Route>
								</Routes>
							</React.Suspense>
						</BrowserRouterCompat>
					</CommandsProvider>
				</ThemeProvider>
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				<Stack
					styles={{root: {height: '100%', width: '100%', position: 'absolute'}}}
				>
					<LoadWrapper loading={true} text={'Redirecting to sign in...'} />
				</Stack>
			</UnauthenticatedTemplate>
		</>
	);
}

export default withStore(App);

initializeIcons(undefined, {disableWarnings: true});
